<template>
  <li
    class="nav-item"
    :class="{ divider: divider, 'nav-dropdown': hasChildren, open: isOpen }"
    @click.stop="toggleOpen"
  >
    <div v-if="divider" />
    <div v-else-if="hasChildren" class="nav-link nav-dropdown-toggle">
      <i v-if="icon" :class="icon" /> {{ label }}
    </div>
    <div v-else>
      <router-link :to="url" class="nav-link">
        <i v-if="icon" :class="icon" /> {{ label }}
      </router-link>
    </div>
    <ul v-if="hasChildren" class="nav-dropdown-items">
      <sidebar-nav-item
        v-for="child in children"
        v-bind="child"
        :key="child.url"
      />
    </ul>
  </li>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { PropType } from 'vue'

defineOptions({ name: 'SidebarNavItem' })

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  divider: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String,
    default: ''
  },
  url: {
    type: String,
    default: ''
  },
  children: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  open: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()

const currentOpen = ref(props.open)

const hasChildren = computed(() => {
  return (props.children && props.children.length)
})

const isOpen = computed(() => {
  return hasChildren.value && currentOpen.value
})

// Auto-open the section the user is currently inside, auto-collapse
// the rest. `props.url` for a top-level item is its route prefix
// (e.g. `/view`, `/config`); the active route's path will start with
// `${url}/` when we're inside that section. Manual click-to-expand
// still works (toggleOpen below), but the next route change re-runs
// this rule — so non-active sections always end up collapsed once
// the user navigates again.
const isActive = computed(() =>
  Boolean(hasChildren.value && props.url && route.path.startsWith(`${props.url}/`))
)

watch(isActive, (active) => {
  currentOpen.value = active
}, { immediate: true })

function toggleOpen () {
  currentOpen.value = !currentOpen.value
}
</script>
